import { computed, Injectable, Signal } from '@angular/core';
import { select } from '@ngxs/store';
import { BusinessServices, IUser, Roles } from '@shs-digi-care-monorepo/interfaces';
import { Stage } from '@shs-digi-care-monorepo/frontend-lib/enums';
import { roleNamesForStream } from '@shs-digi-care-monorepo/frontend-lib/util';
import { UserState } from '@shs-digi-care-monorepo/frontend-lib';
import { environment } from '../../../environments/environment';
import { TASK_OWNER, FEATURE_CONFIG } from './nam-feature.config';
import { NamFeature } from './nam-feature.model';

/**
 * Service that centralizes role-based feature gating for the NAM module.
 *
 * Instead of scattering `isTaskOwner(user)` checks across components, inject this
 * service and check roles and features via signals. The service reads the current
 * user from the store automatically — no need to pass user around. Roles are scoped
 * to the OM NAM stream, so a role held in another stream does not leak into NAM.
 *
 * @example
 * ```ts
 * // In a component:
 * private readonly features = inject(NamFeatureService);
 *
 * protected isTaskOwner = this.features.isTaskOwner;
 * protected canSeeAllocation = this.features.isEnabled(NamFeature.ALLOCATION);
 * ```
 *
 * @example
 * ```html
 * <!-- In a template: -->
 * @if (canSeeAllocation()) {
 *   <button (click)="requestAllocation()">Request allocation</button>
 * }
 * ```
 */
@Injectable({
    providedIn: 'root'
})
export class NamFeatureService {
    private readonly me: Signal<IUser | null> = select(UserState.me);

    /** Role names the current user holds within the OM NAM stream. */
    private readonly namRoles: Signal<Roles[]> = computed(() => roleNamesForStream(this.me(), BusinessServices.OM_NAM));

    /**
     * `true` when the current user administers the OM NAM stream. An admin of
     * another stream (e.g. OM EMEA) must not be granted NAM admin rights.
     */
    public readonly isAdmin: Signal<boolean> = computed(() => this.namRoles().includes(Roles.ADMIN));

    /** `true` when the current user may act as a task owner in the OM NAM stream. */
    public readonly isTaskOwner: Signal<boolean> = computed(() =>
        this.namRoles().some((role) => TASK_OWNER.includes(role))
    );

    /** `true` when the current user is an internal user of the OM NAM stream. */
    public readonly isInternal: Signal<boolean> = computed(() => this.isTaskOwner() || !!this.me()?.isInternal);

    /** `true` when the current user is the assignee of the given request. */
    public isAssignee(request: { assignee?: string | null } | null | undefined): boolean {
        const gid = this.me()?.gid;
        return !!gid && !!request && request.assignee === gid;
    }

    /**
     * Synchronously checks whether the current user holds the given role in the
     * OM NAM stream. Reads the user signal, so calls made inside a `computed`
     * stay reactive.
     */
    public hasRole(role: Roles): boolean {
        return this.namRoles().includes(role);
    }

    /**
     * Returns a computed signal that evaluates to `true` when the given feature
     * is enabled for the current user (based on their role and the current stage).
     */
    public isEnabled(feature: NamFeature): Signal<boolean> {
        return computed(() => this.evaluate(feature));
    }

    /**
     * Synchronously checks whether the given feature is enabled for the current user.
     * Reads the user signal, so calls made inside a `computed`/`effect` stay reactive.
     */
    public isActive(feature: NamFeature): boolean {
        return this.evaluate(feature);
    }

    /**
     * Returns a computed signal that evaluates to `true` when ALL given features
     * are enabled for the current user.
     */
    public allEnabled(...features: NamFeature[]): Signal<boolean> {
        return computed(() => features.every((f) => this.evaluate(f)));
    }

    /**
     * Returns a computed signal that evaluates to `true` when ANY of the given features
     * is enabled for the current user.
     */
    public anyEnabled(...features: NamFeature[]): Signal<boolean> {
        return computed(() => features.some((f) => this.evaluate(f)));
    }

    private evaluate(feature: NamFeature): boolean {
        const user = this.me();
        if (!user) return false;

        const config = FEATURE_CONFIG[feature];

        if (config.disabledOnStages?.includes(environment.stage as Stage)) {
            return false;
        }

        if (config.roles.length === 0) {
            return true;
        }

        const held = this.namRoles();
        return config.roles.some((role) => held.includes(role)) || (!!config.includeInternalUsers && !!user.isInternal);
    }
}
